import { useState, useEffect } from "react";
import { domainTemplateApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { TOOLSET_CATALOG } from "./toolsetCatalog";

// Phase C per agent-builder-v1.md §5.5 + §10 rows 16-18. Inline expert
// authoring wizard. Opens when a Sub-Agent node is dropped on the canvas
// with no source agent. Four steps:
//
//   1. Identity     — name, description, channels (default inherits parent)
//   2. Persona      — system_prompt via AI-assist / catalog / free-text
//   3. Grammar      — domain_grammar source (catalog / null / custom JSON)
//   4. Tools        — toolset opt-in matrix
//
// On Save (step 4): inserts a new expert via PUT /api/agents/<supervisor>/tree
// (C2 atomic save; C18 round-trip), then patches the canvas node's
// agentConfig with the new expert's id + name. Supervisor's
// flow_config.supervisor.experts array is updated by the same /tree call.
//
// Trigger from BuilderView's handleCanvasDrop:
//   - new agent node with agentConfig.agentId === null AND
//   - currentAgentId !== null (must have a saved supervisor row to attach)

const STEPS = ["Identity", "Persona", "Grammar", "Tools"];
const ALL_CHANNELS = ["chat", "voice", "whatsapp", "sms", "email"];
// D5: sourced from the one canonical toolset catalog (shared with WiringTab)
// so the picker can't drift from the other surfaces. Keeps the {id, label}
// shape this wizard renders.
const ALL_TOOLSETS = TOOLSET_CATALOG.map((t) => ({ id: t.name, label: t.label }));

const inputStyle = {
  width: "100%", padding: "8px 10px", borderRadius: 8,
  border: `1px solid ${COLORS.border}`, background: COLORS.surface,
  color: COLORS.text, fontSize: 12, outline: "none", boxSizing: "border-box",
};

const Card = ({ title, children }) => (
  <div style={{
    padding: 14, borderRadius: 10,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    display: "flex", flexDirection: "column", gap: 10,
  }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>
    {children}
  </div>
);

export default function ExpertWizard({ open, onClose, parentSupervisor, parentChannels, currentUser, onCreated }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Step 1
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [channels, setChannels] = useState({});

  // Step 2
  const [personaMode, setPersonaMode] = useState("free_text");  // ai | catalog | free_text
  const [systemPrompt, setSystemPrompt] = useState("");
  const [aiHint, setAiHint] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [personaCatalog, setPersonaCatalog] = useState([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState(null);

  // Step 3
  const [grammarMode, setGrammarMode] = useState("none");  // catalog | none | custom
  const [grammarCatalog, setGrammarCatalog] = useState([]);
  const [selectedGrammarSlug, setSelectedGrammarSlug] = useState(null);
  const [grammarJson, setGrammarJson] = useState("{}");

  // Step 4
  const [toolsetEnabled, setToolsetEnabled] = useState({});

  const isAdmin = ["owner", "administrator"].includes(currentUser?.role);

  useEffect(() => {
    if (!open) return;
    setStep(0); setErr(null); setBusy(false);
    setName(""); setDescription("");
    setChannels(Object.fromEntries((parentChannels || []).map(c => [c, true])));
    setPersonaMode("free_text"); setSystemPrompt(""); setAiHint("");
    setSelectedPersonaId(null);
    setGrammarMode("none"); setSelectedGrammarSlug(null); setGrammarJson("{}");
    setToolsetEnabled({});
  }, [open, parentChannels]);

  // Load grammar + persona catalogs lazily.
  useEffect(() => {
    if (!open) return;
    domainTemplateApi.list().then((res) => {
      const list = res?.templates || res?.data?.templates || [];
      setGrammarCatalog(list);
      setPersonaCatalog(list);
    }).catch(() => { setGrammarCatalog([]); setPersonaCatalog([]); });
  }, [open]);

  if (!open) return null;

  const next = () => {
    setErr(null);
    if (step === 0 && !name.trim()) { setErr("Name is required."); return; }
    if (step === 1 && personaMode !== "ai" && !systemPrompt.trim()) { setErr("System prompt is required (or use AI-assist)."); return; }
    if (step === 2 && grammarMode === "custom" && !isAdmin) { setErr("Custom JSON grammar is admin-only."); return; }
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep(s => Math.max(0, s - 1));

  const generatePersona = async () => {
    if (!aiHint.trim()) return;
    setAiBusy(true); setErr(null);
    try {
      // Reuses C3's generate_expert_prompt tool through the Super Agent's
      // chat surface. For a one-shot LLM call, hit the model_resolver via
      // a direct chat to the Super Agent and ask it to produce the prompt.
      // Lean v1: we just pre-fill from the hint and let the user refine
      // via the AI assist panel — full BYOK chat round-trip is wired
      // through chatApi when the user opens the panel.
      setSystemPrompt(`You are an expert in ${aiHint.trim()}. Operate inside a Supervisor's L3 fan-out pipeline. Stay within scope, cite sources, refuse out-of-domain questions, and return evidence with citations.`);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setAiBusy(false);
    }
  };

  const finalPersona = () => {
    if (personaMode === "catalog" && selectedPersonaId) {
      const tpl = personaCatalog.find(t => (t.id || t.slug) === selectedPersonaId);
      if (tpl?.system_prompt) return tpl.system_prompt;
    }
    return systemPrompt;
  };

  const finalGrammar = () => {
    if (grammarMode === "catalog" && selectedGrammarSlug) {
      const tpl = grammarCatalog.find(t => (t.slug || t.id) === selectedGrammarSlug);
      return tpl?.domain_grammar || tpl?.grammar || null;
    }
    if (grammarMode === "custom") {
      try { return JSON.parse(grammarJson); } catch { return null; }
    }
    return null;  // grammarMode === "none"
  };

  const onSave = async () => {
    // §19.6 — wizard captures spec into canvas state; the actual DB
    // write happens when the user clicks the Builder's Save button
    // (apply_tree creates supervisor + all unsaved experts in one
    // transaction). Per §5.5 ¶7 design intent.
    setBusy(true); setErr(null);
    try {
      const channelList = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
      const toolsets = Object.entries(toolsetEnabled).filter(([, v]) => v).map(([k]) => k);

      // Pass the full spec back via onCreated. BuilderView patches the
      // canvas node's agentConfig with these fields; collectUnsavedExpertPayloads
      // reads them at Save time.
      const spec = {
        // _canvasOnly signals "no agent_id yet — Save creates the row."
        _canvasOnly: true,
        name: name.trim(),
        description: description.trim() || null,
        system_prompt: finalPersona(),
        channels: channelList,
        toolsets,
        domain_grammar: finalGrammar(),
        authoredVia: "expert_wizard",
      };
      onCreated && onCreated(spec);
      onClose && onClose();
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={{
        width: 600, maxWidth: "100%", maxHeight: "90vh",
        background: COLORS.surface, borderRadius: 14,
        border: `1px solid ${COLORS.border}`,
        display: "flex", flexDirection: "column", overflow: "hidden",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{
          padding: "14px 18px", borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.surface, display: "flex", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>{"\u{1F916}"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text }}>New Sub-Agent</div>
            <div style={{ fontSize: 10, color: COLORS.textDim }}>step {step + 1}/{STEPS.length} \u00b7 {STEPS[step]}</div>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: COLORS.textDim,
            cursor: "pointer", fontSize: 18,
          }}>{"\u2715"}</button>
        </div>

        {/* Step indicator */}
        <div style={{ padding: "10px 18px", display: "flex", gap: 6 }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? COLORS.accent : COLORS.border,
            }} />
          ))}
        </div>

        {/* Body */}
        <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {err && <div style={{ padding: "8px 12px", borderRadius: 8, background: `${COLORS.pink}18`, color: COLORS.text, fontSize: 12, border: `1px solid ${COLORS.pink}55` }}>{err}</div>}

          {step === 0 && (
            <>
              <Card title="Identity">
                <div>
                  <label style={{ fontSize: 10, color: COLORS.textDim, display: "block", marginBottom: 4 }}>Name *</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Contract Risk Analyst" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: COLORS.textDim, display: "block", marginBottom: 4 }}>Description</label>
                  <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Short summary of the expert's scope" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 10, color: COLORS.textDim, display: "block", marginBottom: 4 }}>
                    Channels {parentChannels?.length ? "(default: inherits parent)" : ""}
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {ALL_CHANNELS.map(c => {
                      const on = !!channels[c];
                      return (
                        <button key={c} onClick={() => setChannels(prev => ({ ...prev, [c]: !on }))} style={{
                          padding: "4px 10px", borderRadius: 6, fontSize: 11,
                          border: `1px solid ${on ? COLORS.accent : COLORS.border}`,
                          background: on ? COLORS.accentSoft : "transparent",
                          color: on ? COLORS.text : COLORS.textMuted, cursor: "pointer",
                        }}>{c}</button>
                      );
                    })}
                  </div>
                </div>
              </Card>
            </>
          )}

          {step === 1 && (
            <Card title="Persona / system prompt">
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  ["ai",        "AI-assist"],
                  ["catalog",   "Catalog"],
                  ["free_text", "Free-text"],
                ].map(([id, label]) => (
                  <button key={id} onClick={() => setPersonaMode(id)} style={{
                    padding: "5px 12px", borderRadius: 6, fontSize: 11,
                    border: `1px solid ${personaMode === id ? COLORS.accent : COLORS.border}`,
                    background: personaMode === id ? COLORS.accentSoft : "transparent",
                    color: personaMode === id ? COLORS.text : COLORS.textMuted, cursor: "pointer",
                  }}>{label}</button>
                ))}
              </div>

              {personaMode === "ai" && (
                <>
                  <input value={aiHint} onChange={e => setAiHint(e.target.value)}
                    placeholder="What should this expert focus on? e.g. 'analyze contract risk clauses'"
                    style={inputStyle} />
                  <button onClick={generatePersona} disabled={!aiHint.trim() || aiBusy} style={{
                    alignSelf: "flex-start",
                    padding: "6px 14px", borderRadius: 6, border: "none",
                    background: COLORS.accent, color: "#fff",
                    fontSize: 11, fontWeight: 600,
                    cursor: aiHint.trim() && !aiBusy ? "pointer" : "not-allowed",
                  }}>{aiBusy ? "Generating\u2026" : "Generate prompt"}</button>
                </>
              )}

              {personaMode === "catalog" && (
                <select value={selectedPersonaId || ""} onChange={e => {
                  const id = e.target.value;
                  setSelectedPersonaId(id);
                  const tpl = personaCatalog.find(t => (t.id || t.slug) === id);
                  if (tpl?.system_prompt) setSystemPrompt(tpl.system_prompt);
                }} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="">Pick a catalog persona\u2026</option>
                  {personaCatalog.map(t => (
                    <option key={t.id || t.slug} value={t.id || t.slug}>{t.name || t.slug}</option>
                  ))}
                </select>
              )}

              <textarea value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)}
                placeholder="System prompt the expert runs under\u2026"
                style={{ ...inputStyle, minHeight: 140, resize: "vertical", fontFamily: "inherit" }} />
            </Card>
          )}

          {step === 2 && (
            <Card title="Domain grammar">
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
                The grammar carries entity / relationship / Cypher hints used by the L3 knowledge subagent. Pick a catalog grammar, leave it null (generic), or paste a custom JSON (admin-only).
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  ["catalog", "Catalog"],
                  ["none",    "Null (generic)"],
                  ["custom",  isAdmin ? "Custom JSON" : "Custom JSON (admin only)"],
                ].map(([id, label]) => (
                  <button key={id}
                    disabled={id === "custom" && !isAdmin}
                    onClick={() => setGrammarMode(id)} style={{
                      padding: "5px 12px", borderRadius: 6, fontSize: 11,
                      border: `1px solid ${grammarMode === id ? COLORS.accent : COLORS.border}`,
                      background: grammarMode === id ? COLORS.accentSoft : "transparent",
                      color: grammarMode === id ? COLORS.text : COLORS.textMuted,
                      cursor: id === "custom" && !isAdmin ? "not-allowed" : "pointer",
                      opacity: id === "custom" && !isAdmin ? 0.4 : 1,
                  }}>{label}</button>
                ))}
              </div>

              {grammarMode === "catalog" && (
                <select value={selectedGrammarSlug || ""} onChange={e => setSelectedGrammarSlug(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                  <option value="">Pick a catalog grammar\u2026</option>
                  {grammarCatalog.map(t => (
                    <option key={t.slug || t.id} value={t.slug || t.id}>{t.name || t.slug}</option>
                  ))}
                </select>
              )}

              {grammarMode === "custom" && isAdmin && (
                <textarea value={grammarJson} onChange={e => setGrammarJson(e.target.value)}
                  placeholder='{"entities":[...],"relationships":[...],"cypher_query_templates":[...]}'
                  style={{ ...inputStyle, minHeight: 140, resize: "vertical", fontFamily: "monospace" }} />
              )}
            </Card>
          )}

          {step === 3 && (
            <Card title="Toolsets">
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5 }}>
                Internal toolsets the expert can call. Each toolset wires a set of tools through the LangGraph pipeline at runtime.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {ALL_TOOLSETS.map(t => {
                  const on = !!toolsetEnabled[t.id];
                  return (
                    <label key={t.id} style={{
                      display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: COLORS.text,
                      padding: "5px 0", cursor: "pointer",
                    }}>
                      <input type="checkbox" checked={on}
                        onChange={() => setToolsetEnabled(prev => ({ ...prev, [t.id]: !on }))} />
                      <span>{t.label}</span>
                      <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>{t.id}</span>
                    </label>
                  );
                })}
              </div>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 18px", borderTop: `1px solid ${COLORS.border}`,
          background: COLORS.surface, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button onClick={onClose} style={{
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.textDim, padding: "6px 14px", borderRadius: 8,
            fontSize: 11, cursor: "pointer",
          }}>Cancel</button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button onClick={back} style={{
                background: "transparent", border: `1px solid ${COLORS.border}`,
                color: COLORS.text, padding: "6px 14px", borderRadius: 8,
                fontSize: 11, cursor: "pointer",
              }}>Back</button>
            )}
            {step < STEPS.length - 1 ? (
              <button onClick={next} style={{
                padding: "6px 16px", borderRadius: 8, border: "none",
                background: COLORS.accent, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}>Next</button>
            ) : (
              <button onClick={onSave} disabled={busy} style={{
                padding: "6px 16px", borderRadius: 8, border: "none",
                background: COLORS.green, color: "#fff", fontSize: 11, fontWeight: 600,
                cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
              }}>{busy ? "Saving\u2026" : "Save expert"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
